import { createInstance, type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';
import zhCN from './zh-CN.json';
import type en from './en.json';

/** Locales with a translation. Must stay equal to the `Locale` enum of `@wiztime/core` (data dictionary §2). */
export const SUPPORTED_LOCALES = ['zh-CN', 'en'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** The default locale; its translation is part of the first-load script. */
export const DEFAULT_LOCALE: SupportedLocale = 'zh-CN';

export function isSupportedLocale(value: unknown): value is SupportedLocale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

type ZhCN = typeof zhCN;
type En = typeof en;
/** Compile-time check: the English resource has exactly the keys of the Chinese one. */
type AssertSameKeys<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never;
export const resourcesAreComplete: AssertSameKeys<ZhCN, En> = true;

/** The translation tree of one locale (typed after the Chinese one, which every locale must match). */
export type Translation = ZhCN;

declare module 'i18next' {
  interface CustomTypeOptions {
    defaultNS: 'translation';
    resources: { translation: ZhCN };
  }
}

/**
 * Translations by locale. Only the default one is bundled with the first-load script; the others
 * are separate chunks fetched when a user starts in that language or switches to it.
 */
const loaders: Record<SupportedLocale, () => Promise<Translation>> = {
  'zh-CN': () => Promise.resolve(zhCN),
  en: async () => (await import('./en.json')).default,
};

const loaded = new Map<SupportedLocale, Translation>([[DEFAULT_LOCALE, zhCN]]);

/** The translation of `locale`, fetched on first use. */
export async function loadTranslation(locale: SupportedLocale): Promise<Translation> {
  const known = loaded.get(locale);
  if (known !== undefined) return known;
  const translation = await loaders[locale]();
  loaded.set(locale, translation);
  return translation;
}

/**
 * A configured i18next instance for `locale`, ready synchronously. The translation of `locale`
 * must be loaded already (`loadTranslation`; the default locale always is): otherwise it throws.
 */
export function initI18n(locale: SupportedLocale = DEFAULT_LOCALE): I18nInstance {
  const translation = loaded.get(locale);
  if (translation === undefined) throw new Error(`The translation of ${locale} is not loaded`);
  const instance = createInstance();
  void instance.use(initReactI18next).init({
    lng: locale,
    fallbackLng: DEFAULT_LOCALE,
    resources: {
      [DEFAULT_LOCALE]: { translation: zhCN },
      [locale]: { translation },
    },
    interpolation: { escapeValue: false },
    initAsync: false,
  });
  return instance;
}

/** Like `initI18n`, but loads the translation of `locale` first. */
export async function createI18n(locale: SupportedLocale = DEFAULT_LOCALE): Promise<I18nInstance> {
  await loadTranslation(locale);
  return initI18n(locale);
}

/** Change the language of a running instance, loading and adding the translation when it is new to it. */
export async function switchLocale(instance: I18nInstance, locale: SupportedLocale): Promise<void> {
  const translation = await loadTranslation(locale);
  if (!instance.hasResourceBundle(locale, 'translation')) {
    instance.addResourceBundle(locale, 'translation', translation);
  }
  await instance.changeLanguage(locale);
}

export type { I18nInstance };
